import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import type { Breed } from 'shared/@generated/models';
import type { BreedMarketData } from 'shared/@types/breed-market-data';
import { BreedInput } from 'shared/components/form/breed-input';
import { DropdownSelect } from 'shared/components/form/dropdown-select';
import type { BaseModalProps } from 'shared/components/base-modal';
import { FullscreenModal } from 'shared/components/fullscreen-modal';
import { FilterModalFooter } from 'shared/components/web-filter-controls';
import { Text } from 'shared/components/text';
import { TextInput } from 'shared/components/form/text-input';
import {
  PetCostAnimal,
  PetCostSettings,
  careLevels,
  foodLevels,
  groomingLevels,
  insuranceLevels,
} from 'shared/library/pet-costs';

const numberFormatter = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 });

export function PetCostBudgetModal({
  animal,
  settings,
  breedId,
  breedName,
  marketStats,
  onSelectBreed,
  onClearBreed,
  onChange,
  onReset,
  trigger,
}: {
  animal: PetCostAnimal;
  settings: PetCostSettings;
  breedId: number | null;
  breedName?: string;
  onSelectBreed: (breed: Breed) => void;
  onClearBreed: () => void;
  marketStats: BreedMarketData | null;
  onChange: (changes: Partial<PetCostSettings>) => void;
  onReset: () => void;
  trigger: BaseModalProps['trigger'];
}) {
  const { t } = useTranslation('screens');

  return (
    <FullscreenModal
      centered={true}
      headerTitle={t('pet_costs.customize')}
      contentTestID={'pet-costs-budget-modal'}
      trigger={trigger}
      footer={({ close }) => (
        <FilterModalFooter
          clearLabel={t('pet_costs.modal.reset')}
          applyLabel={t('pet_costs.modal.done')}
          applyTestID={'pet-costs-budget-done'}
          onClear={onReset}
          onApply={close}
        />
      )}
    >
      <View className={'gap-7'}>
        <Field label={t('pet_costs.breed_label')}>
          <BreedInput
            animal={animal}
            allowQuery={false}
            historyEnabled={false}
            value={breedId ? [breedId] : []}
            onSelectBreed={onSelectBreed}
            onChange={(values) => {
              if (values.length === 0) onClearBreed();
            }}
          />
        </Field>

        <Field label={t('pet_costs.modal.purchase.label')}>
          <View className={'flex-row items-center gap-2'}>
            <TextInput
              value={settings.purchasePrice ? numberFormatter.format(settings.purchasePrice) : ''}
              inputMode={'numeric'}
              keyboardType={'number-pad'}
              accessibilityLabel={t('pet_costs.modal.purchase.label')}
              testID={'pet-costs-purchase-input'}
              className={'flex-1'}
              onChangeText={(text) => {
                const digits = text.replace(/\D/g, '').slice(0, 6);

                onChange({ purchasePrice: Number(digits) || 0 });
              }}
            />
            <Text className={'text-brand-brown-dark font-semibold'}>
              {t('pet_costs.amount', { amount: '', currency: '' }).trim()}
            </Text>
          </View>
          {marketStats && breedName && (
            <Text className={'text-brand-brown-dark/60 mt-1.5 text-sm'}>
              {t('pet_costs.modal.purchase.hint_market', {
                breed: breedName,
                min: numberFormatter.format(marketStats.typical_price_min),
                max: numberFormatter.format(marketStats.typical_price_max),
              })}
            </Text>
          )}
        </Field>

        <Field label={t('pet_costs.modal.food.label')}>
          <Options
            title={t('pet_costs.modal.food.label')}
            options={foodLevels}
            value={settings.food}
            label={(option) => t(`pet_costs.modal.food.options.${animal}.${option}`)}
            onChange={(food) => onChange({ food })}
          />
        </Field>

        <Field label={t('pet_costs.modal.insurance.label')}>
          <Options
            title={t('pet_costs.modal.insurance.label')}
            options={insuranceLevels}
            value={settings.insurance}
            label={(option) => t(`pet_costs.modal.insurance.options.${option}`)}
            onChange={(insurance) => onChange({ insurance })}
          />
        </Field>

        {animal === 'dog' && (
          <Field label={t('pet_costs.modal.grooming.label')}>
            <Options
              title={t('pet_costs.modal.grooming.label')}
              options={groomingLevels}
              value={settings.grooming}
              label={(option) => t(`pet_costs.modal.grooming.options.${option}`)}
              onChange={(grooming) => onChange({ grooming })}
            />
          </Field>
        )}

        <Field label={t(`pet_costs.modal.care.label.${animal}`)}>
          <Options
            title={t(`pet_costs.modal.care.label.${animal}`)}
            options={careLevels[animal]}
            value={settings.care}
            label={(option) => t(`pet_costs.modal.care.options.${animal}.${option}`)}
            onChange={(care) => onChange({ care })}
          />
        </Field>

        {animal === 'dog' && (
          <Field label={t('pet_costs.modal.training.label')}>
            <Options
              title={t('pet_costs.modal.training.label')}
              options={['yes', 'no'] as const}
              value={settings.training ? 'yes' : 'no'}
              label={(option) => t(`pet_costs.modal.training.options.${option}`)}
              onChange={(option) => onChange({ training: option === 'yes' })}
            />
          </Field>
        )}
      </View>
    </FullscreenModal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <View>
      <Text className={'text-brand-brown-dark mb-1 font-bold'}>{label}</Text>
      {children}
    </View>
  );
}

function Options<T extends string>({
  title,
  options,
  value,
  label,
  onChange,
}: {
  title: string;
  options: readonly T[];
  value: T;
  label: (option: T) => string;
  onChange: (value: T) => void;
}) {
  return (
    <DropdownSelect<T>
      title={title}
      options={options.map((option) => ({ value: option, label: label(option) }))}
      value={value}
      onChange={(option) => option && onChange(option)}
    />
  );
}
